import API_BASE_URL from "../config";

/**
 * Service to handle all backend API requests.
 */

// Persistent mock data for development
let mockAdmins = [
  { email: "admin@example.com", name: "Mock Admin" },
  { email: "super@example.com", name: "Super User" },
];

const apiService = {
  /**
   * Fetches the Salesforce access token and instance URL.
   */
  getSalesforceToken: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/get-salesforce-token`);
      if (!response.ok) {
        throw new Error("Failed to fetch Salesforce token");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.getSalesforceToken error:", error);
      throw error;
    }
  },

  /**
   * Fast extraction from email body for the Context tab.
   */
  analyzeEmailContext: async ({ emailBody, messageId, userEmail }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/analyze-context`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailBody, messageId, userEmail }),
      });
      if (!response.ok) {
        throw new Error("Context analysis failed");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.analyzeEmailContext error:", error);
      throw error;
    }
  },

  /**
   * Detailed analysis for the AI Analysis tab.
   */
  analyzeEmailDetailed: async ({ emailBody, messageId, userEmail }) => {
    try {
      const formData = new FormData();
      formData.append("emailBody", emailBody || "");
      formData.append("messageId", messageId || "");
      formData.append("userEmail", userEmail || "");

      const response = await fetch(`${API_BASE_URL}/api/analyze-email`, {
        method: "POST",
        body: formData,
      });
      if (!response.ok) {
        throw new Error("Detailed analysis failed");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.analyzeEmailDetailed error:", error);
      throw error;
    }
  },

  /**
   * Calls the backend to generate cross-sell opportunities from Salesforce data.
   */
  generateCrossSellOpportunities: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cross-sell-opportunities`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Cross-sell generation failed");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.generateCrossSellOpportunities error:", error);
      throw error;
    }
  },

  /**
   * Calls the backend to match an account in Salesforce.
   */
  matchAccount: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/match-account`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Account matching failed");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.matchAccount error:", error);
      throw error;
    }
  },
  /**
   * Calls the backend to search for an account in Salesforce manually.
   */
  searchAccount: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/search-account`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Account search failed");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.searchAccount error:", error);
      throw error;
    }
  },

  /**
   * Calls the backend to create a case in Salesforce.
   */
  createCase: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/create-case`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Case creation failed");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.createCase error:", error);
      throw error;
    }
  },

  /**
   * Calls the backend to generate a follow-up email draft using AI.
   */
  generateFollowup: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/generate-followup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Follow-up generation failed");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.generateFollowup error:", error);
      throw error;
    }
  },

  /**
   * Calls the backend to schedule a follow-up call in Salesforce.
   */
  scheduleFollowUpCall: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/followup-call`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Failed to schedule follow-up call");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.scheduleFollowUpCall error:", error);
      throw error;
    }
  },
  /**
   * Calls the backend to create a task in Salesforce.
   */
  createTask: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/create-task`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Failed to create task");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.createTask error:", error);
      throw error;
    }
  },

  /**
   * Calls the backend to log an email activity in Salesforce.
   */
  logEmailToSalesforce: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/log-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Failed to log email activity");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.logEmailToSalesforce error:", error);
      throw error;
    }
  },


  /**
   * Fetches all templates (agency + user's personal) from the database.
   */
  getTemplates: async (userEmail = "") => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/templates?userEmail=${encodeURIComponent(userEmail)}`);
      if (!response.ok) {
        throw new Error("Failed to fetch templates");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.getTemplates error:", error);
      throw error;
    }
  },

  /**
   * Fetches all template categories.
   */
  getCategories: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/categories`);
      if (!response.ok) {
        throw new Error("Failed to fetch categories");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.getCategories error:", error);
      throw error;
    }
  },

  /**
   * Creates a new template (personal or agency if admin).
   */
  createTemplate: async (payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/templates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Failed to create template");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.createTemplate error:", error);
      throw error;
    }
  },

  /**
   * Updates an existing template by ID.
   */
  updateTemplate: async (id, payload) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/templates/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error("Failed to update template");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.updateTemplate error:", error);
      throw error;
    }
  },

  /**
   * Soft-deletes a template by ID.
   */
  deleteTemplate: async (id, userEmail) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/templates/${id}?userEmail=${encodeURIComponent(userEmail)}`,
        { method: "DELETE" }
      );
      if (!response.ok) {
        throw new Error("Failed to delete template");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.deleteTemplate error:", error);
      throw error;
    }
  },

  /**
   * Checks if the current user email is an admin.
   */
  checkAdmin: async (userEmail) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/is-admin?userEmail=${encodeURIComponent(userEmail)}`);
      if (!response.ok) {
        throw new Error("Failed to check admin status");
      }
      return await response.json();
    } catch (error) {
      console.error("apiService.checkAdmin error:", error);
      return { isAdmin: false };
    }
  },

  /**
   * Ensures the current Outlook user exists in the backend users table.
   */
  registerOutlookUser: async ({ userEmail, userName }) => {
    try {
      /*
      const response = await fetch(`${API_BASE_URL}/api/register-outlook-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userEmail: userEmail || "",
          userName: userName || "",
        }),
      });
      if (!response.ok) {
        throw new Error("Failed to register Outlook user");
      }
      return await response.json();
      */
      console.log("Mocking registerOutlookUser for:", userEmail);
      return { status: "success", message: "User registered (mocked)" };
    } catch (error) {
      console.error("apiService.registerOutlookUser error:", error);
      throw error;
    }
  },

  /**
   * Fetches the effective admin users for the Outlook plugin.
   */
  getAdminUsers: async () => {
    try {
      /*
      const response = await fetch(`${API_BASE_URL}/api/admin-users`);
      if (!response.ok) {
        throw new Error("Failed to fetch admin users");
      }
      return await response.json();
      */
      console.log("Mocking getAdminUsers");
      return {
        admins: [...mockAdmins],
      };
    } catch (error) {
      console.error("apiService.getAdminUsers error:", error);
      throw error;
    }
  },

  /**
   * Fetches Outlook users for admin management.
   */
  getOutlookUsers: async (userEmail) => {
    try {
      /*
      const response = await fetch(`${API_BASE_URL}/api/outlook-users?userEmail=${encodeURIComponent(userEmail || "")}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch Outlook users");
      }
      return await response.json();
      */
      console.log("Mocking getOutlookUsers for:", userEmail);
      return {
        users: [
          { email: "agent1@example.com", name: "John Doe" },
          { email: "agent2@example.com", name: "Jane Smith" },
          { email: "agent3@example.com", name: "Bob Johnson" },
        ],
      };
    } catch (error) {
      console.error("apiService.getOutlookUsers error:", error);
      throw error;
    }
  },

  /**
   * Updates an Outlook user's admin role.
   */
  updateAdminRole: async (userEmail, payload) => {
    try {
      /*
      const response = await fetch(`${API_BASE_URL}/api/admin-users/role?userEmail=${encodeURIComponent(userEmail || "")}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update admin role");
      }
      return await response.json();
      */
      console.log("Mocking updateAdminRole for:", payload.targetEmail, "to", payload.role);

      if (payload.role === "admin") {
        // Check if already in admins
        if (!mockAdmins.some((a) => a.email === payload.targetEmail)) {
          mockAdmins.push({
            email: payload.targetEmail,
            name: payload.targetName || payload.targetEmail,
          });
        }
      } else if (payload.role === "user") {
        // Remove from admins
        mockAdmins = mockAdmins.filter((a) => a.email !== payload.targetEmail);
      }

      return {
        admins: [...mockAdmins],
      };
    } catch (error) {
      console.error("apiService.updateAdminRole error:", error);
      throw error;
    }
  },

  /**
   * Fetches Outlook analytics for the current user scope and selected view.
   */
  getAnalytics: async (userEmail, range = "7d", view = "agent") => {
    try {
      /*
      const params = new URLSearchParams({
        userEmail: userEmail || "",
        range,
        view,
      });
      const response = await fetch(`${API_BASE_URL}/api/analytics?${params.toString()}`);
      if (!response.ok) {
        throw new Error("Failed to fetch analytics");
      }
      return await response.json();
      */
      console.log(`Mocking getAnalytics: range=${range}, view=${view}`);

      // Adjust numbers based on range to make the UI feel reactive
      let multiplier = 1;
      if (range === "today") multiplier = 0.15;
      else if (range === "7d") multiplier = 1;
      else if (range === "30d") multiplier = 4.2;
      else if (range === "90d") multiplier = 12.5;

      const summary = {
        avgAnalysisMinutes: range === "today" ? 1.2 : 1.4,
        emailsHandled: Math.round(128 * multiplier),
        detailedCompletionPercent: range === "today" ? 95 : 92,
      };

      const rows =
        view === "agent"
          ? [
              {
                key: "1",
                displayName: "John Doe",
                userEmail: "agent1@example.com",
                emailsHandled: Math.round(45 * multiplier),
                detailedCompletionPercent: 95,
                avgAnalysisMinutes: 1.2,
              },
              {
                key: "2",
                displayName: "Jane Smith",
                userEmail: "agent2@example.com",
                emailsHandled: Math.round(38 * multiplier),
                detailedCompletionPercent: 88,
                avgAnalysisMinutes: 1.6,
              },
              {
                key: "3",
                displayName: "Bob Johnson",
                userEmail: "agent3@example.com",
                emailsHandled: Math.round(45 * multiplier),
                detailedCompletionPercent: 93,
                avgAnalysisMinutes: 1.4,
              },
            ]
          : [
              {
                key: "c1",
                category: "Inquiry",
                emailsHandled: Math.round(50 * multiplier),
                detailedCompletionPercent: 90,
                avgAnalysisMinutes: 1.1,
              },
              {
                key: "c2",
                category: "Support",
                emailsHandled: Math.round(40 * multiplier),
                detailedCompletionPercent: 95,
                avgAnalysisMinutes: 1.8,
              },
              {
                key: "c3",
                category: "Claim",
                emailsHandled: Math.round(38 * multiplier),
                detailedCompletionPercent: 91,
                avgAnalysisMinutes: 1.3,
              },
            ];

      return {
        isAdmin: true,
        summary,
        rows,
      };
    } catch (error) {
      console.error("apiService.getAnalytics error:", error);
      throw error;
    }
  },

  /**
   * Fetches or creates an Outlook user by email.
   */
  getOrCreateUser: async (email, name) => {
    try {
      /*
      const response = await fetch(`${API_BASE_URL}/api/settings/user?email=${encodeURIComponent(email)}&name=${encodeURIComponent(name || "")}`);
      if (!response.ok) {
        throw new Error("Failed to fetch/create user");
      }
      return await response.json();
      */
      console.log("Mocking getOrCreateUser for:", email);
      return {
        email: email || "mockuser@example.com",
        name: name || "Mock User",
        role: "admin",
      };
    } catch (error) {
      console.error("apiService.getOrCreateUser error:", error);
      throw error;
    }
  },

  /**
   * Fetches global settings.
   */
  getUserSettings: async () => {
    try {
      /*
      const response = await fetch(`${API_BASE_URL}/api/settings/`);
      if (!response.ok) {
        throw new Error("Failed to fetch settings");
      }
      return await response.json();
      */
      console.log("Mocking getUserSettings");
      return {
        apiConfiguration: {
          sfdcConsumerKey: "mock_consumer_key_xyz",
          sfdcConsumerSecret: "mock_consumer_secret_abc",
          masterSubscriptionEmail: "admin@agency.com",
        },
      };
    } catch (error) {
      console.error("apiService.getUserSettings error:", error);
      throw error;
    }
  },

  /**
   * Saves user settings.
   */
  saveUserSettings: async (email, settings) => {
    try {
      /*
      const response = await fetch(`${API_BASE_URL}/api/settings/?email=${encodeURIComponent(email)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to save settings");
      }
      return await response.json();
      */
      console.log("Mocking saveUserSettings for:", email, settings);
      return { status: "success", message: "Settings saved (mocked)" };
    } catch (error) {
      console.error("apiService.saveUserSettings error:", error);
      throw error;
    }
  },
};

export default apiService;
